// 링크 수정 — 목적지·만료·클릭제한·비밀번호 (IMP-18b)
//
// 링크를 새로 만들지 않고 고칠 수 있어야 한다. 목적지 오타 하나 때문에 링크를 다시 만들면
// 이미 배포한 짧은 주소가 죽는다 — 단축 링크의 가치가 사라진다.
//
// PATCH는 null을 "바꾸지 않음"으로 읽으므로, **해제**는 clearExpireAt·clearMaxClicks로 보낸다.
import { useEffect, useRef, useState, type FormEvent } from "react";
import { linkApi, type Link, type UpdateLinkInput } from "../api/links.ts";
import { ApiError } from "@taplink/web-shared";

interface Props {
  link: Link;
  /** 만료·클릭제한은 유료 기능(ACCESS_CONTROL). 꺼져 있으면 **해제만** 허용한다 */
  accessControlEnabled: boolean;
  onSaved: () => void;
  onClose: () => void;
}

/** ISO(UTC) → datetime-local 입력값(로컬 시각). 초·타임존은 버린다 */
function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function LinkEditDialog({ link, accessControlEnabled, onSaved, onClose }: Props) {
  const [originalUrl, setOriginalUrl] = useState(link.originalUrl);
  const [expireAt, setExpireAt] = useState(toLocalInput(link.expireAt));
  const [maxClicks, setMaxClicks] = useState(link.maxClicks?.toString() ?? "");
  const [password, setPassword] = useState("");
  const [removePassword, setRemovePassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const firstFieldRef = useRef<HTMLInputElement>(null);

  // 다이얼로그가 열리면 첫 입력으로 초점을 옮긴다 — 키보드 사용자가 헤매지 않도록
  useEffect(() => {
    firstFieldRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const hadExpire = link.expireAt != null;
  const hadMaxClicks = link.maxClicks != null;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSaving(true);

    // 비운 채로 저장 = 해제. 원래 값이 없었다면 해제할 것도 없다.
    // 두 플래그는 계약상 필수라 항상 보낸다(서버 기본값은 false — UpdateLinkRequest).
    const patch: UpdateLinkInput = {
      clearExpireAt: expireAt === "" && hadExpire,
      clearMaxClicks: maxClicks === "" && hadMaxClicks,
      clearPassword: removePassword,
    };
    let changed = patch.clearExpireAt || patch.clearMaxClicks || patch.clearPassword;

    const url = originalUrl.trim();
    if (url !== link.originalUrl) {
      patch.originalUrl = url;
      changed = true;
    }

    if (expireAt !== "") {
      const iso = new Date(expireAt).toISOString();
      if (iso !== link.expireAt) {
        patch.expireAt = iso;
        changed = true;
      }
    }

    if (maxClicks !== "" && Number(maxClicks) !== link.maxClicks) {
      patch.maxClicks = Number(maxClicks);
      changed = true;
    }

    // 비밀번호는 현재 값을 알 수 없으므로(해시만 저장) 입력이 있을 때만 보낸다
    if (!removePassword && password !== "") {
      patch.password = password;
      changed = true;
    }

    if (!changed) {
      onClose(); // 바뀐 게 없으면 요청하지 않는다
      return;
    }

    try {
      await linkApi.update(link.shortKey, patch);
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "수정에 실패했습니다.");
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="edit-title">링크 수정</h2>
        <p className="hint">
          <code>{link.shortUrl}</code> — 짧은 주소는 바뀌지 않습니다.
        </p>

        {error && <div className="alert alert-error" role="alert">{error}</div>}

        <form onSubmit={onSubmit}>
          <div className="field">
            <label htmlFor="edit-url">원본 URL</label>
            <input
              id="edit-url"
              ref={firstFieldRef}
              type="url"
              required
              value={originalUrl}
              onChange={(e) => setOriginalUrl(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="edit-expire">만료 일시</label>
            <input
              id="edit-expire"
              type="datetime-local"
              value={expireAt}
              disabled={!accessControlEnabled && !hadExpire}
              onChange={(e) => setExpireAt(e.target.value)}
              aria-describedby="edit-expire-hint"
            />
            <p className="hint" id="edit-expire-hint">
              {hadExpire ? "비우면 만료 없이 유지됩니다." : "비워 두면 만료되지 않습니다."}
            </p>
          </div>

          <div className="field">
            <label htmlFor="edit-max">최대 클릭 수</label>
            <input
              id="edit-max"
              type="number"
              min={1}
              placeholder="제한 없음"
              value={maxClicks}
              disabled={!accessControlEnabled && !hadMaxClicks}
              onChange={(e) => setMaxClicks(e.target.value)}
              aria-describedby="edit-max-hint"
            />
            <p className="hint" id="edit-max-hint">
              {hadMaxClicks ? "비우면 제한이 해제됩니다." : "비워 두면 제한이 없습니다."}
            </p>
          </div>

          {!accessControlEnabled && (hadExpire || hadMaxClicks) && (
            <p className="hint">
              현재 요금제에서는 만료·클릭제한을 새로 걸 수 없습니다. 이미 설정된 값의 <strong>해제</strong>는 가능합니다.
            </p>
          )}

          <div className="field">
            <label htmlFor="edit-password">
              {link.hasPassword ? "새 비밀번호" : "비밀번호"}
            </label>
            <input
              id="edit-password"
              type="password"
              autoComplete="new-password"
              placeholder={link.hasPassword ? "변경하지 않으려면 비워 두세요" : "설정 안 함"}
              value={password}
              disabled={removePassword || (!accessControlEnabled && !link.hasPassword)}
              onChange={(e) => setPassword(e.target.value)}
              aria-describedby="edit-password-hint"
            />
            <p className="hint" id="edit-password-hint">
              8자 이상, 영문·숫자·기호 중 두 종류 이상. 짧은 주소는 이미 배포되었을 수 있어
              바뀌지 않으므로, 비밀번호가 유일한 방어선입니다.
            </p>
            {link.hasPassword && (
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={removePassword}
                  onChange={(e) => setRemovePassword(e.target.checked)}
                />
                비밀번호 해제 (누구나 열 수 있게)
              </label>
            )}
          </div>

          {/* 여백은 CSS 가 정한다 — 인라인 style 은 규칙보다 세서 나중에 손댈 수 없다 */}
          <div className="row-actions modal-actions">
            <button type="submit" className="btn-primary" disabled={saving || !originalUrl.trim()}>
              {saving ? "저장 중…" : "저장"}
            </button>
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
              취소
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
